import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Stack,
  Text,
  Group,
  NumberInput,
  Textarea,
  Button,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { DEFAULT_CURRENCY } from '../../constants/payment.consts';

interface RecordDepositModalProps {
  opened: boolean;
  onClose: () => void;
  remainingBalance: number;
  recordDeposit: (amount: number, note?: string) => Promise<void>;
}

export function RecordDepositModal({
  opened,
  onClose,
  remainingBalance,
  recordDeposit,
}: RecordDepositModalProps) {
  const { t } = useTranslation('lims');

  const [depositForm, setDepositForm] = useState({ amount: 0, note: '' });
  const [depositSaving, setDepositSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleDeposit = async () => {
    if (depositForm.amount <= 0) {
      return;
    }
    try {
      setDepositSaving(true);
      setLocalError(null);
      await recordDeposit(depositForm.amount, depositForm.note);
      handleClose();
    } catch (e: any) {
      setLocalError(e.message || 'Failed to record deposit');
    } finally {
      setDepositSaving(false);
    }
  };

  const handleClose = () => {
    setDepositForm({ amount: 0, note: '' });
    setLocalError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('payment.recordDeposit')}
      centered
      size="sm"
    >
      <Stack gap="md">
        {localError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {localError}
          </Alert>
        )}

        <NumberInput
          label={t('payment.labels.depositAmount')}
          min={0}
          max={remainingBalance}
          suffix={` ${DEFAULT_CURRENCY}`}
          value={depositForm.amount}
          onChange={(v) =>
            setDepositForm((prev) => ({ ...prev, amount: Number(v) || 0 }))
          }
        />

        <Text size="xs" c="dimmed">
          {t('payment.remainingAfterDeposit', {
            amount: Math.max(0, remainingBalance - depositForm.amount).toFixed(
              2
            ),
            currency: DEFAULT_CURRENCY,
          })}
        </Text>

        <Textarea
          label={t('payment.labels.noteText')}
          placeholder={t('payment.placeholders.enterNote')}
          value={depositForm.note}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDepositForm((prev) => ({ ...prev, note: value }));
          }}
          minRows={2}
          autosize
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            {t('payment.cancel')}
          </Button>
          <Button
            onClick={handleDeposit}
            loading={depositSaving}
            disabled={depositForm.amount <= 0}
          >
            {t('payment.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
